import React from 'react';
import type { ComponentType } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import Image from '@atlaskit/image';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';

import unCheckedIconLight from './images/unCheckedIcon-Light.svg';
import unCheckedIconDark from './images/unCheckedIcon-Dark.svg';
import checkedIconLight from './images/checkedIcon-Light.svg';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	width: 420,
	display: 'flex',
	marginTop: token('space.150', '12px'),
	marginBottom: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomIconWrapper = styled.div({
	paddingTop: token('space.150', '12px'),
	paddingRight: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CoreCheckIconWrapper = styled.div({
	marginTop: token('space.025', '2px'),
	paddingTop: token('space.100', '8px'),
	paddingRight: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomImageWrapper = styled.div({
	width: 40,
	paddingRight: token('space.200', '16px'),
	paddingTop: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CoreIconBackground = styled.div({
	backgroundColor: token('color.background.information'),
	height: 36,
	width: 36,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: token('space.075', '6px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	width: 364,
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextAreaTitle = styled.div({
	font: token('font.body'),
	fontWeight: token('font.weight.bold'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextAreaBody = styled.div({
	font: token('font.body'),
});

export enum CheckIconStatus {
	CHECKED,
	UNCHECKED,
	NOT_RENDERED,
}

export type ListItemProps = {
	bodyText: MessageDescriptor;
	darkImg: string;
	img: string;
	icon: ComponentType<NewCoreIconProps>;
	title: MessageDescriptor;
	checkIconStatus?: CheckIconStatus;
};

export const ListItem = ({
	img,
	darkImg,
	icon,
	title,
	bodyText,
	checkIconStatus = CheckIconStatus.NOT_RENDERED,
}: ListItemProps) => {
	const Icon = icon;

	return (
		<Wrapper>
			{checkIconStatus === CheckIconStatus.UNCHECKED &&
				(fg('platform-visual-refresh-icons') ? (
					<CoreCheckIconWrapper>
						<CheckCircleIcon
							label=""
							testId="unchecked-icon"
							spacing="spacious"
							color={token('color.icon')}
						/>
					</CoreCheckIconWrapper>
				) : (
					<CustomIconWrapper>
						<Image src={unCheckedIconLight} srcDark={unCheckedIconDark} testId="unchecked-icon" />
					</CustomIconWrapper>
				))}
			{checkIconStatus === CheckIconStatus.CHECKED &&
				(fg('platform-visual-refresh-icons') ? (
					<CoreCheckIconWrapper>
						<CheckCircleIcon
							label=""
							testId="checked-icon"
							spacing="spacious"
							color={token('color.icon.success', '#22A06B')}
						/>
					</CoreCheckIconWrapper>
				) : (
					<CustomIconWrapper>
						<Image src={checkedIconLight} testId="checked-icon" />
					</CustomIconWrapper>
				))}
			{fg('platform-visual-refresh-icons') ? (
				<Box paddingBlockStart="space.050" paddingInlineEnd="space.200">
					<CoreIconBackground>
						<Icon label="" color={token('color.icon.information')} />
					</CoreIconBackground>
				</Box>
			) : (
				<CustomImageWrapper>
					<Image src={img} srcDark={darkImg} />
				</CustomImageWrapper>
			)}
			<ContentWrapper>
				<TextAreaTitle>
					<FormattedMessage {...title} />
				</TextAreaTitle>
				<TextAreaBody>
					<FormattedMessage {...bodyText} />
				</TextAreaBody>
			</ContentWrapper>
		</Wrapper>
	);
};
